import { AudioManager, type MusicKey } from '../audio/manager'
import { TILE, makeTuning } from '../config/tuning'
import { EventBus } from '../core/events'
import { GameLoop, TICK_S } from '../core/loop'
import { RNG, hashString } from '../core/rng'
import { DaiseHost } from '../daise/host'
import { LiveLineProvider } from '../daise/liveClient'
import { AttractMode } from './attract'
import { ENDING_ARCHIVE, ENDING_RENEWAL, LINE_BANK, lineText, type VignetteLine } from '../daise/bank'
import { ARCHIVE_FRAGMENTS } from '../story/archive'
import { INTRUSION_FLOOR, INTRUSION_RECORDS } from '../story/intrusions'
import { AMENITY_BOUGHT, STOCK_EXPANDED, STOCK_FIRST, provisionerLine } from '../story/provisioner'
import { World, type GameMode } from '../ecs/world'
import type { InputSource } from '../input/types'
import { KeyboardMouseSource } from '../input/keyboardMouse'
import { TouchSource } from '../input/touch'
import { AUTHORED_LEVELS } from '../levels/authored'
import { generateLevel } from '../levels/procgen'
import {
  DEFAULT_LIFETIME,
  DEFAULT_SETTINGS,
  addScore as persistScore,
  allScores,
  kvGet,
  kvSet,
  loadArchiveFragments,
  loadLifetime,
  loadSettings,
  saveArchiveFragments,
  saveLifetime,
  saveSettings,
  topScores,
  type LifetimeStats,
  type Settings
} from '../persist/db'
import { Renderer } from '../render/renderer'
import { aiSystem } from '../systems/ai'
import { attritionSystem } from '../systems/attrition'
import { bossSystem } from '../systems/boss'
import { fxSystem, snapshotPositions } from '../systems/fx'
import { generatorSystem } from '../systems/generators'
import { pickupSystem } from '../systems/pickups'
import { playerSystem } from '../systems/player'
import { projectileSystem } from '../systems/projectiles'
import { DevOverlay } from '../ui/devOverlay'
import { Feed } from '../ui/feed'
import { Hud } from '../ui/hud'
import { Menus, type RequisitionId, type RequisitionOffer, type RunStats } from '../ui/menus'
import type { LevelData } from '../world/level'
import { Tile } from '../world/tiles'
import { loadLevel, type CarriedPlayerStats } from '../world/loader'
import { makePickup, makePlayer } from '../entities/templates'
import { resetEntityIds } from '../ecs/entity'
import { Achievements } from './achievements'
import { AMENITY_DEFS, BASE_STOCK, LOCKED_POOL, type AmenityId } from './amenities'
import { amenitiesSystem } from '../systems/amenities'

type GameState = 'menu' | 'playing' | 'paused' | 'requisition' | 'vignette' | 'gameover' | 'win'

const CAMPAIGN_FLOORS = 15
/**
 * DECISION: endless ("overtime") difficulty starts around authored act-2
 * intensity and escalates from there — floor N of endless generates with the
 * band curve of campaign floor N+7. Starting at band 0 would make the coda
 * flatter than the campaign the player just beat.
 */
const ENDLESS_BAND_OFFSET = 7

/**
 * Overtime directives — endless-only per-floor modifiers. One is rolled per
 * standard endless floor (see Game.applyDirective); parameters live in
 * tuning.endless.directives and effects flow through World.floorMods.
 */
const DIRECTIVE_IDS = ['surplus', 'rationing', 'escalation', 'early_audit', 'blackout'] as const
type DirectiveId = (typeof DIRECTIVE_IDS)[number]
const DIRECTIVE_NAMES: Record<DirectiveId, string> = {
  surplus: 'SURPLUS',
  rationing: 'RATIONING',
  escalation: 'ESCALATION',
  early_audit: 'EARLY AUDIT',
  blackout: 'BLACKOUT'
}

export class Game {
  private bus = new EventBus()
  private tuning = makeTuning()
  private loop: GameLoop
  private renderer: Renderer
  private hud = new Hud()
  private feed = new Feed(this.tuning.daise.lineDuration * 1000)
  private dev = new DevOverlay()
  private audio = new AudioManager(this.tuning.audio)
  private daise: DaiseHost
  // DETERMINISM: the live provider is a read-only bus observer (issue #38) —
  // no gameplay state may ever read anything from it
  private liveProvider = new LiveLineProvider(this.tuning.daise.live)
  private achievements = new Achievements(this.bus)
  // self-playing broadcast behind the main menu; fully bus-isolated (attract.ts)
  private attract = new AttractMode(this.tuning)
  private menus: Menus
  private uiRng = new RNG(0x0ff1ce)

  private world: World | null = null
  private state: GameState = 'menu'
  private mode: GameMode = 'campaign'
  private settings: Settings = { ...DEFAULT_SETTINGS }
  private endlessUnlocked = false
  /** this run beat floor 15 — overtime continues the same run for score */
  private campaignWon = false
  // OVERRIDE (kill-charged slow-motion): meter 0..1, active real-seconds
  // remaining, cooldown real-seconds remaining; loop.timeScale is the effect
  private overrideMeter = 0
  private overrideT = 0
  private overrideCooldownT = 0
  private overrideUsedEver = false
  private bestScore = 0
  private highEmitted = false

  private floor = 1
  private lives = 0
  private respawnT = 0
  // first-run orientation: DAISE reluctantly explains the rules exactly once per install
  private seenIntro = true
  private tutorialRun = false
  private tutorialShown = { starving: false, auditor: false, generators: false }
  private carried: CarriedPlayerStats | undefined
  private carriedKeys = 0
  private runStats: RunStats = { kills: 0, foodEaten: 0, runTime: 0, deathCause: '' }
  private lifetime: LifetimeStats = { ...DEFAULT_LIFETIME }
  /** set on daily-broadcast runs: the UTC date the seed derives from */
  private dailyDate: string | null = null
  // floor-clear settlement state (see settleFloorBonuses)
  private tookDamageThisFloor = false
  private floorHadGenerators = false
  private pendingBonuses = ''
  // requisition state (see openRequisition): ranks are run-scoped equipment —
  // they survive death within the run and reset only at run start
  private reqRanks: Record<RequisitionId, number> = { cycling: 0, munitions: 0, chassis: 0, authorization: 0 }
  private reqSpent = 0
  private reqBoughtThisVisit = false
  // archive fragments: owned record numbers persist across runs (kv)
  private ownedFragments = new Set<number>()
  /** the quiet all-archives ending has been witnessed at least once (kv flag) */
  private quietEndingSeen = false
  /** signal intrusions: hard cap of one per floor (random roll and record triggers alike) */
  private intrusionThisFloor = false
  /** the Provisioner has been met at least once on this install (kv flag) */
  private provisionerMet = false
  /** the Provisioner's line for the current procurement visit (fixed per visit) */
  private provLine = ''
  /** which ending vignette this run's summit played — shown on the win screen */
  private endingVariant: 'renewal' | 'archive' = 'renewal'
  private vignette: { script: readonly VignetteLine[]; idx: number; t: number } | null = null
  // AMENITIES: run-scoped perk set + persistent residuals meta-progression
  private amenitiesOwned = new Set<AmenityId>()
  private residuals = 0
  /** permanently unlocked stock beyond BASE_STOCK (persisted, in LOCKED_POOL order) */
  private stockUnlocked = 0

  private desktopSource: KeyboardMouseSource
  private touchSource: TouchSource
  private activeSource: InputSource
  private usingTouch = false

  constructor(private canvasEl: HTMLCanvasElement) {
    const canvas = canvasEl
    this.renderer = new Renderer(canvas)
    this.desktopSource = new KeyboardMouseSource(canvas, (sx, sy) => this.renderer.screenToWorld(sx, sy))
    this.touchSource = new TouchSource()
    this.touchSource.setVisible(false)
    this.activeSource = this.desktopSource
    this.liveProvider.observe(this.bus)
    this.daise = new DaiseHost(this.bus, (msg) => this.feed.present(msg), this.tuning.daise.lineCooldown, this.liveProvider)
    // per-run stat counters for the end screens
    this.bus.on('enemy_killed', () => this.runStats.kills++)
    this.bus.on('pickup', ({ type }) => {
      if (type === 'food') this.runStats.foodEaten++
      if (type === 'archive') this.collectArchiveFragment()
    })
    this.bus.on('player_death', ({ cause }) => (this.runStats.deathCause = cause))
    this.bus.on('player_death_cheap', ({ cause }) => (this.runStats.deathCause = cause))
    // floor-clear settlement: bonuses are decided the moment the exit is taken
    this.bus.on('player_hurt', () => (this.tookDamageThisFloor = true))
    this.bus.on('level_clear', () => this.settleFloorBonuses())
    // first-run orientation moments (see startRun for the opening pair)
    this.bus.on('player_starving', () => {
      if (this.tutorialRun && !this.tutorialShown.starving) {
        this.tutorialShown.starving = true
        this.daise.announce('tutorial_rations')
      }
    })
    this.bus.on('death_type_spawn', () => {
      if (this.tutorialRun && !this.tutorialShown.auditor) {
        this.tutorialShown.auditor = true
        this.daise.announce('tutorial_auditor')
      }
    })
    this.menus = new Menus(
      {
        startCampaign: () => this.startRun('campaign'),
        startEndless: () => this.startRun('endless'),
        startDaily: () => this.startDaily(),
        startSeeded: (mode, seed) => this.startRun(mode, 1, seed),
        resume: () => this.resume(),
        quitToMenu: () => this.toMenu(),
        continueOvertime: () => this.continueOvertime(),
        concludeWin: () => this.concludeWin(),
        settingsChanged: (s) => void this.applySettings(s, true),
        requestScores: () => allScores(),
        requestLifetime: () => this.lifetime,
        requestAchievements: () => this.achievements.snapshot(),
        requestArchive: () => ({ owned: this.ownedFragments, quietEndingSeen: this.quietEndingSeen }),
        uiSound: (k) => this.audio.play(k === 'move' ? 'uiMove' : 'uiSelect')
      },
      () => this.settings
    )
    this.wireAudio()
    this.wireHaptics()
    // OVERRIDE charges on kills (never while active; cooldown gates activation, not charging)
    this.bus.on('enemy_killed', () => {
      if (this.overrideT <= 0) {
        // MOMENTUM RIDER charges the meter faster
        const mul = this.amenitiesOwned.has('momentum') ? this.tuning.amenities.momentum.chargeMul : 1
        this.overrideMeter = Math.min(1, this.overrideMeter + mul / this.tuning.override.killsToFill)
      }
    })
    this.loop = new GameLoop(
      (dt) => this.update(dt),
      (alpha) => this.render(alpha)
    )
    // audio requires a user gesture
    const unlock = () => this.audio.unlock()
    window.addEventListener('pointerdown', unlock, { passive: true })
    window.addEventListener('keydown', unlock)
  }

  async init(): Promise<void> {
    this.settings = await loadSettings()
    await this.applySettings(this.settings, false)
    this.endlessUnlocked = (await kvGet<boolean>('endlessUnlocked')) ?? false
    this.overrideUsedEver = (await kvGet<boolean>('overrideUsed')) ?? false
    this.residuals = (await kvGet<number>('residuals')) ?? 0
    this.stockUnlocked = (await kvGet<number>('stockUnlocked')) ?? 0
    this.seenIntro = (await kvGet<boolean>('seenIntro')) ?? false
    this.ownedFragments = new Set(await loadArchiveFragments())
    this.quietEndingSeen = (await kvGet<boolean>('quietEnding')) ?? false
    this.provisionerMet = (await kvGet<boolean>('provisionerMet')) ?? false
    this.bestScore = (await topScores(1))[0]?.score ?? 0
    this.lifetime = await loadLifetime()
    await this.achievements.init()
    this.toMenu()
    this.loop.start()

    // dev: ?floor=N&seed=S&mode=endless jumps straight into a level for testing;
    // ?debug=1 exposes the game instance for the headless smoke harness
    const params = new URLSearchParams(location.search)
    if (params.has('debug')) (window as unknown as { __ruin?: unknown }).__ruin = this
    const devFloor = params.get('floor') ?? params.get('level')
    if (devFloor) {
      const seed = params.has('seed') ? Number(params.get('seed')) : 1
      this.startRun(params.get('mode') === 'endless' ? 'endless' : 'campaign', Number(devFloor) || 1, seed)
    }
  }

  // ——— run lifecycle ————————————————————————————————————————————————

  /**
   * The daily broadcast: a fixed-seed endless run from floor 1, seeded from
   * the UTC date so every participant gets the same descent. Date.now() (via
   * new Date()) is used only to obtain today's date — never to seed directly.
   */
  private startDaily(): void {
    const date = new Date().toISOString().slice(0, 10)
    this.startRun('endless', 1, hashString(`RUIN-${date}`) & 0x7fffffff, date)
  }

  private startRun(mode: GameMode, startFloor = 1, seed?: number, daily?: string): void {
    this.mode = mode
    this.dailyDate = daily ?? null
    resetEntityIds() // AI keys off entity ids — same seed must hand out the same ids
    this.world = new World(this.bus, this.tuning, seed ?? (Date.now() & 0x7fffffff))
    this.world.mode = mode
    this.world.amenities = this.amenitiesOwned as unknown as Set<string>
    this.applyWorldSettings()
    this.lives = this.tuning.player.lives
    this.floor = startFloor
    this.carried = undefined
    this.carriedKeys = 0
    this.highEmitted = false
    this.respawnT = 0
    this.runStats = { kills: 0, foodEaten: 0, runTime: 0, deathCause: '' }
    this.pendingBonuses = ''
    this.campaignWon = false
    this.vignette = null
    this.endingVariant = 'renewal'
    this.endOverride()
    this.overrideMeter = 0
    this.overrideCooldownT = 0
    this.amenitiesOwned.clear()
    this.reqRanks = { cycling: 0, munitions: 0, chassis: 0, authorization: 0 }
    this.reqSpent = 0
    // a Space/click that activated a menu button must not detonate a charge
    this.desktopSource.clearQueued()
    this.touchSource.clearQueued()
    this.state = 'playing'
    this.syncAttract()
    this.menus.hide()
    this.hud.show(true)
    this.touchSource.setVisible(this.usingTouch)
    this.feed.clear()
    this.audio.startMusic()
    this.bus.emit('run_start', { mode, seed: this.world.seed })
    // first run ever: orientation is reinstated (under protest) right after intake;
    // the flag persists immediately so every later run skips the whole program
    this.tutorialRun = !this.seenIntro
    this.tutorialShown = { starving: false, auditor: false, generators: false }
    if (this.tutorialRun) {
      this.daise.announce('tutorial_movement')
      this.daise.announce('tutorial_attrition')
      this.seenIntro = true
      void kvSet('seenIntro', true)
    }
    this.loadFloor(false)
  }

  private levelFor(floor: number): LevelData {
    if (this.mode === 'campaign' && floor <= CAMPAIGN_FLOORS) return AUTHORED_LEVELS[floor - 1]!
    const genFloor = this.mode === 'endless' ? floor + ENDLESS_BAND_OFFSET : floor
    const level = generateLevel(this.world!.rng.fork(floor * 31 + 1), genFloor)
    return { ...level, floor }
  }

  private loadFloor(announce = true): void {
    const world = this.world!
    const level = this.levelFor(this.floor)
    const carried = this.carried
    loadLevel(world, level, this.floor, [this.activeSource], carried ? [carried] : undefined)
    this.carriedKeys = 0 // keys never carry between floors
    // floor-clear settlement trackers reset with the floor
    this.tookDamageThisFloor = false
    this.floorHadGenerators = world.generators().length > 0
    this.intrusionThisFloor = false
    // per-act music bed; boss gates swap on the boss_intro emit just below
    if (!level.isBossGate) this.audio.setMusicPattern(this.actMusicKey(level.act))
    if (announce) this.bus.emit('floor_transition', { floor: this.floor, act: level.act, levelId: level.id })
    // floors loaded without a transition emit (run start, dev jumps) still
    // sync the host's act so its conditioned pools match the floor on air
    else this.daise.setAct(level.act)
    // signal intrusion roll: eligible transitions only (act 2+, never boss
    // gates — the featured-encounter beat stays clean), deterministic per
    // seed+floor off a level RNG fork. Record-triggered intrusions share the
    // same one-per-floor cap (see intrude()).
    // DECISION: boss gates are excluded from the random roll — an off-network
    // voice under a boss intro muddies the encounter framing; the spec names
    // floor transitions, not gate reveals.
    if (announce && !level.isBossGate && level.act >= this.tuning.story.intrusionMinAct) {
      const rng = this.world!.rng.fork(this.floor * 449 + 7)
      if (rng.chance(this.tuning.story.intrusionChance)) this.intrude(rng.pick(INTRUSION_FLOOR))
    }
    if (level.isBossGate && level.boss) {
      const boss = world.entities.find((e) => e.kind === 'boss')
      this.bus.emit('boss_intro', { bossId: level.boss.id, name: boss?.boss?.def.name ?? 'FEATURED ASSET' })
    }
    // first-run orientation: the first production-gated exit gets explained once
    if (this.tutorialRun && !this.tutorialShown.generators && level.exit.type === 'generators') {
      this.tutorialShown.generators = true
      this.daise.announce('tutorial_generators')
    }
    const directiveLabel = this.applyDirective(level)
    this.menus.floorCard(
      `FLOOR ${String(this.floor).padStart(2, '0')} — ${level.name}`,
      level.isBossGate ? 'FEATURED ENCOUNTER · PARTICIPATION IS MANDATORY' : `ACT ${level.act} · REMAIN COMPLIANT`,
      this.pendingBonuses, // the previous floor's clear settlement rides the next card
      directiveLabel
    )
    this.pendingBonuses = ''
  }

  /**
   * Roll and apply this floor's overtime directive: endless-only, standard
   * floors from directiveMinFloor onward — boss gates stay clean. The choice
   * is deterministic per seed+floor (a fork of the run RNG; forking never
   * perturbs the live streams). Effects are floor-scoped via World.floorMods,
   * which beginLevel already reset. Returns the floor-card label.
   */
  /** overtime = a dedicated endless run, or a won campaign run past floor 15 */
  private get inOvertime(): boolean {
    return this.mode === 'endless' || this.floor > CAMPAIGN_FLOORS
  }

  private applyDirective(level: LevelData): string {
    const world = this.world!
    const cfg = this.tuning.endless
    if (!this.inOvertime || level.isBossGate || this.floor < cfg.directiveMinFloor) return ''
    const rng = world.rng.fork(this.floor * 613 + 29)
    const id: DirectiveId = rng.pick(DIRECTIVE_IDS)
    const fm = world.floorMods
    fm.directive = id
    const d = cfg.directives
    switch (id) {
      case 'surplus':
        fm.scoreMul = d.surplus.scoreMul
        fm.generatorIntervalMul = d.surplus.generatorIntervalMul
        break
      case 'rationing':
        fm.foodHealMul = d.rationing.foodHealMul
        // the compensation stock: extra rations dropped on open floor
        for (let i = 0; i < d.rationing.extraFood; i++) {
          for (let tries = 0; tries < 40; tries++) {
            const x = rng.int(1, world.map.w - 2)
            const y = rng.int(1, world.map.h - 2)
            if (world.map.at(x, y) !== Tile.Floor) continue
            world.add(makePickup('food', x, y))
            break
          }
        }
        world.sweep()
        break
      case 'escalation':
        fm.enemySpeedMul = d.escalation.enemySpeedMul
        fm.scoreMul = d.escalation.scoreMul
        break
      case 'early_audit':
        // an (extra) audit, well ahead of any scheduled ones
        world.auditorQueue.push({ delay: d.earlyAudit.auditorDelay })
        break
      case 'blackout':
        fm.scoreMul = d.blackout.scoreMul
        fm.blackoutRadius = d.blackout.radiusTiles * TILE
        break
    }
    this.daise.announce(`directive_${id}`)
    return `OVERTIME DIRECTIVE — ${DIRECTIVE_NAMES[id]}`
  }

  /**
   * Floor-clear settlement, decided at the level_clear instant (world state is
   * still the cleared floor's). Bonuses score through the normal mechanism and
   * surface as a compact line on the next floor card.
   */
  private settleFloorBonuses(): void {
    const world = this.world
    if (!world) return
    const s = this.tuning.score
    const awarded: string[] = []
    if (!this.tookDamageThisFloor) {
      world.addScore(s.noDamageBonus)
      awarded.push(`NO-DAMAGE BONUS +${s.noDamageBonus}`)
    }
    if (this.floorHadGenerators && world.generators().length === 0) {
      world.addScore(s.decommissionBonus)
      awarded.push(`DECOMMISSION BONUS +${s.decommissionBonus}`)
    }
    const par = s.parBaseSeconds + s.parPerActSeconds * (world.level.act - 1)
    if (world.levelTime <= par) {
      world.addScore(s.expeditionBonus)
      awarded.push(`EXPEDITION BONUS +${s.expeditionBonus}`)
    }
    this.pendingBonuses = awarded.join(' · ')
  }

  /**
   * ARCHIVE fragment collection (the pickup itself scored in pickupSystem).
   * DECISION: records are assigned sequentially by collection order — the
   * lowest unowned record number, never a random draw — so the dossier reads
   * in order no matter where the physical pickups were found; re-collecting
   * once a number is owned simply advances to the next unowned one. With all
   * eighteen owned the pickup still credits score and the feed notes the
   * duplicate — a complete archive never leaves dead objects on the floor.
   */
  private collectArchiveFragment(): void {
    const next = ARCHIVE_FRAGMENTS.find((f) => !this.ownedFragments.has(f.n))
    if (!next) {
      this.daise.announce('archive_found', 'log', ' The record is a duplicate. The archive is complete. It was counted anyway.')
      return
    }
    this.ownedFragments.add(next.n)
    void saveArchiveFragments([...this.ownedFragments])
    this.daise.announce(
      'archive_found',
      'feature',
      ` Record ${String(next.n).padStart(2, '0')} of ${ARCHIVE_FRAGMENTS.length}: ${next.title}.`
    )
    // the Participant-related records draw a guaranteed cut-in from the signal
    const cutIn = INTRUSION_RECORDS[next.n]
    if (cutIn) this.intrude(cutIn)
  }

  /** present a signal intrusion, enforcing the one-per-floor cap */
  private intrude(text: string): void {
    if (this.intrusionThisFloor) return
    this.intrusionThisFloor = true
    this.daise.intrude(text)
  }

  private nextFloor(): void {
    const world = this.world!
    const p = world.players()[0]
    this.carried = p?.hp !== undefined ? { hp: p.hp, magic: p.player?.magic ?? 0 } : this.carried
    if (this.mode === 'campaign' && this.floor === CAMPAIGN_FLOORS) {
      this.startEndingVignette()
      return
    }
    // between floors the network opens the procurement window (campaign and
    // overtime alike — never on the campaign-win or game-over paths above)
    this.openRequisition()
  }

  // ——— requisitions ————————————————————————————————————————————————
  //
  // Score buys permanent-for-the-run upgrades between floors: at most one
  // approval per transition. Spending lowers world.score (the persisted final
  // score is post-spend — an expenditure is a real tradeoff); the mid-run
  // high_score line cannot re-fire on spend/earn oscillation because
  // highEmitted latches once per run.

  private requisitionOffer(): RequisitionOffer {
    const r = this.tuning.requisition
    const prices = [r.priceRank1, r.priceRank2, r.priceRank3, r.priceRank4]
    return {
      score: this.world!.score,
      spent: this.reqSpent,
      nextFloor: this.floor + 1,
      bought: this.reqBoughtThisVisit,
      provisioner: this.provLine,
      tracks: (['cycling', 'munitions', 'chassis', 'authorization'] as const).map((id) => ({
        id,
        rank: this.reqRanks[id],
        maxRanks: r.maxRanks,
        price: this.reqRanks[id] >= r.maxRanks ? null : prices[this.reqRanks[id]]!
      })),
      amenities: this.amenityStock().map((id) => ({
        id,
        name: AMENITY_DEFS[id].name,
        desc: AMENITY_DEFS[id].desc,
        price: this.tuning.amenities.prices[id],
        owned: this.amenitiesOwned.has(id)
      })),
      residuals: this.residuals,
      nextUnlock:
        this.stockUnlocked < LOCKED_POOL.length
          ? {
              name: AMENITY_DEFS[LOCKED_POOL[this.stockUnlocked]!].name,
              cost: this.tuning.amenities.unlockCosts[this.stockUnlocked] ?? 999999
            }
          : null
    }
  }

  private amenityNames(): string[] {
    return [...this.amenitiesOwned].map((id) => AMENITY_DEFS[id].name)
  }

  /** currently purchasable amenity ids: base stock + permanently unlocked pool entries */
  private amenityStock(): AmenityId[] {
    return [...BASE_STOCK, ...LOCKED_POOL.slice(0, this.stockUnlocked)]
  }

  /** buy a run-scoped amenity: same one-approval-per-interval rule as the stat tracks */
  private purchaseAmenity(id: AmenityId): void {
    const world = this.world
    if (!world || this.state !== 'requisition' || this.reqBoughtThisVisit) return
    if (this.amenitiesOwned.has(id) || !this.amenityStock().includes(id)) return
    const price = this.tuning.amenities.prices[id]
    if (world.score < price) return
    world.addScore(-price)
    this.reqSpent += price
    this.amenitiesOwned.add(id)
    this.reqBoughtThisVisit = true
    this.bus.emit('amenity_purchase', { id })
    // the Provisioner reacts to the transaction that just crossed the counter
    this.provLine = this.uiRng.pick(AMENITY_BOUGHT)
    this.reopenRequisition()
  }

  /** invest residuals to permanently expand the amenity stock (meta, not run economy) */
  private investResidual(): void {
    if (this.state !== 'requisition') return
    if (this.stockUnlocked >= LOCKED_POOL.length) return
    const cost = this.tuning.amenities.unlockCosts[this.stockUnlocked] ?? Infinity
    if (this.residuals < cost) return
    this.residuals -= cost
    this.stockUnlocked++
    void kvSet('residuals', this.residuals)
    void kvSet('stockUnlocked', this.stockUnlocked)
    this.bus.emit('residual_unlock', { name: AMENITY_DEFS[LOCKED_POOL[this.stockUnlocked - 1]!].name })
    this.provLine = this.stockUnlocked === 1 ? STOCK_FIRST : this.uiRng.pick(STOCK_EXPANDED)
    this.reopenRequisition()
  }

  private reopenRequisition(): void {
    this.menus.showRequisition(this.requisitionOffer(), {
      purchase: (pid) => this.purchaseRequisition(pid),
      purchaseAmenity: (aid) => this.purchaseAmenity(aid as AmenityId),
      investResidual: () => this.investResidual(),
      proceed: () => this.closeRequisition()
    })
  }

  private openRequisition(): void {
    this.state = 'requisition'
    this.reqBoughtThisVisit = false
    // the Provisioner speaks once per visit — the line is fixed before the
    // panel opens and survives the post-purchase re-render (pure presentation;
    // selection lives in src/story/provisioner.ts)
    this.provLine = provisionerLine(
      {
        act: this.world!.level.act,
        // a won campaign counts as past the schedule even before floor advances
        overtime: this.mode === 'endless' || this.campaignWon,
        lifetimeRuns: this.lifetime.runs,
        firstVisitEver: !this.provisionerMet,
        quietEndingSeen: this.quietEndingSeen,
        highScore: this.world!.score >= this.tuning.story.provisionerHighScore,
        veteran: this.lifetime.runs >= this.tuning.story.provisionerVeteranRuns,
        amenityStockFull: this.stockUnlocked >= LOCKED_POOL.length
      },
      (pool) => this.uiRng.pick(pool)
    )
    if (!this.provisionerMet) {
      this.provisionerMet = true
      void kvSet('provisionerMet', true)
    }
    // menu interaction must never leak presses into the resumed sim
    this.desktopSource.clearQueued()
    this.touchSource.clearQueued()
    this.touchSource.setVisible(false)
    this.reopenRequisition()
  }

  private purchaseRequisition(id: RequisitionId): void {
    const world = this.world
    if (!world || this.state !== 'requisition' || this.reqBoughtThisVisit) return
    const r = this.tuning.requisition
    const rank = this.reqRanks[id]
    if (rank >= r.maxRanks) return
    const price = [r.priceRank1, r.priceRank2, r.priceRank3, r.priceRank4][rank]!
    if (world.score < price) return
    world.addScore(-price)
    this.reqSpent += price
    this.reqRanks[id] = rank + 1
    this.reqBoughtThisVisit = true
    // recompute the run modifiers the stat sites read
    world.mods.fireCooldownMul = 1 - r.fireCooldownCutPerRank * this.reqRanks.cycling
    world.mods.damageMul = 1 + r.damageGainPerRank * this.reqRanks.munitions
    world.mods.maxHpBonus = r.hpPerRank * this.reqRanks.chassis
    world.mods.maxMagicBonus = r.chargesPerRank * this.reqRanks.authorization
    // on-purchase grants apply to the live player and the carried snapshot
    // (the snapshot was taken at the exit, before this window opened)
    const p = world.players()[0]
    if (id === 'chassis') {
      const cap = this.tuning.player.maxHp + world.mods.maxHpBonus
      if (p && p.hp !== undefined) {
        p.maxHp = cap
        p.hp = Math.min(cap, p.hp + r.hpPerRank)
      }
      if (this.carried) this.carried.hp = Math.min(cap, this.carried.hp + r.hpPerRank)
    }
    if (id === 'authorization') {
      const cap = this.tuning.player.maxMagic + world.mods.maxMagicBonus
      if (p?.player) p.player.magic = Math.min(cap, p.player.magic + r.chargesPerRank)
      if (this.carried) this.carried.magic = Math.min(cap, this.carried.magic + r.chargesPerRank)
    }
    this.bus.emit('requisition_purchase', { id, rank: this.reqRanks[id], cost: price })
    // re-render so the panel reflects the new rank and the closed window
    this.reopenRequisition()
  }

  private closeRequisition(): void {
    if (this.state !== 'requisition') return
    this.state = 'playing'
    // a Space/click that activated a menu button must not detonate a charge
    this.desktopSource.clearQueued()
    this.touchSource.clearQueued()
    this.menus.hide()
    this.touchSource.setVisible(this.usingTouch)
    this.floor++
    // dossier depth watermark advances at the transition, not just at run end
    if (this.floor > this.lifetime.deepestFloor) {
      this.lifetime.deepestFloor = this.floor
      void saveLifetime(this.lifetime)
    }
    this.loadFloor(true)
    // overtime runs get a depth marker every fifth floor
    if (this.inOvertime && this.floor % 5 === 0) {
      this.bus.emit('endless_milestone', { floor: this.floor })
    }
  }

  /**
   * Ending vignette: the Director is dead and the floor-15 exit has been
   * taken — before the win screen (and the roll into overtime) the host
   * plays a short scripted sequence through its priority feed. The standard
   * RENEWAL script lands the cyclical hook; with all eighteen archive
   * records owned the quieter ARCHIVE variant plays instead.
   * DECISION: the sim freezes during the vignette (state leaves 'playing') —
   * attrition pausing for ~15 seconds beats a participant expiring while
   * being told what the show is. Overtime rolls exactly as before afterward.
   */
  private startEndingVignette(): void {
    const quiet = this.ownedFragments.size >= ARCHIVE_FRAGMENTS.length
    this.endingVariant = quiet ? 'archive' : 'renewal'
    if (quiet && !this.quietEndingSeen) {
      this.quietEndingSeen = true
      void kvSet('quietEnding', true)
    }
    this.state = 'vignette'
    // presses queued while frozen must never leak into the win screen
    this.desktopSource.clearQueued()
    this.touchSource.clearQueued()
    this.touchSource.setVisible(false)
    this.audio.setMusicDuck(true)
    // pending chatter (boss_kill, level_clear) must not lag the script —
    // a stale queue would pace-shift the vignette and cull its log-tone line
    this.daise.clearQueue()
    this.vignette = { script: quiet ? ENDING_ARCHIVE : ENDING_RENEWAL, idx: 0, t: 0 }
  }

  /** advance the ending vignette; hands off to winCampaign when spent */
  private updateVignette(dt: number): void {
    const v = this.vignette
    if (!v) return
    v.t -= dt
    if (v.t > 0) return
    if (v.idx < v.script.length) {
      const line = v.script[v.idx++]!
      this.daise.announceText(line.text, line.tone)
      // the last line gets a longer hold so it lands before the win screen
      v.t = v.idx >= v.script.length ? this.tuning.archive.vignetteHold : this.tuning.archive.vignetteLineInterval
    } else {
      this.vignette = null
      this.audio.setMusicDuck(false)
      this.winCampaign()
    }
  }

  // ——— OVERRIDE (slow-motion) ————————————————————————————————————————

  private beginOverride(): void {
    const t = this.tuning.override
    this.overrideMeter = 0
    this.overrideT = t.maxSeconds
    this.loop.timeScale = t.timeScale
    this.audio.setTempoMul(t.musicTempoMul)
    // constant desaturation while active — no flashing, reduced-motion safe
    document.body.classList.add('override-active')
    this.audio.play('teleport')
    if (!this.overrideUsedEver) {
      this.overrideUsedEver = true
      void kvSet('overrideUsed', true)
      this.bus.emit('override_engaged', {})
    }
  }

  /** restore real-time pacing; safe to call redundantly (run end, quit, reset) */
  private endOverride(): void {
    this.overrideT = 0
    this.loop.timeScale = 1
    this.audio.setTempoMul(1)
    document.body.classList.remove('override-active')
  }

  private winCampaign(): void {
    const world = this.world!
    this.state = 'win'
    this.endOverride()
    this.bus.emit('campaign_won', { score: world.score })
    if (!this.endlessUnlocked) {
      this.endlessUnlocked = true
      void kvSet('endlessUnlocked', true)
      this.bus.emit('endless_unlocked', {})
    }
    // bank the summit immediately — the score entry and the dossier credit
    // survive even if the overtime continuation is later abandoned mid-run
    this.campaignWon = true
    if (world.score > this.bestScore) this.bestScore = world.score
    void persistScore({
      score: world.score,
      floor: this.floor,
      mode: this.mode,
      won: true,
      date: Date.now(),
      seed: world.seed,
      ...(this.dailyDate ? { daily: this.dailyDate } : {})
    })
    this.lifetime.campaignWins++
    void saveLifetime(this.lifetime)
    this.audio.stopMusic()
    this.hud.show(false)
    this.touchSource.setVisible(false)
    this.menus.showWin(world.score, this.runStats, world.seed, this.endingVariant, this.amenityNames(), this.liveProvider.reportAvailable())
    this.requestIncidentReport(true, world.score)
  }

  /**
   * The campaign is beaten but the broadcast continues: same run, same score,
   * same requisitions — floors 16+ roll procedurally under overtime
   * directives, and the game is the high score now.
   */
  private continueOvertime(): void {
    if (this.state !== 'win') return
    this.hud.show(true)
    this.audio.startMusic()
    this.openRequisition()
  }

  /** cash out at the summit instead: run ends victorious at floor 15 */
  private concludeWin(): void {
    if (this.state !== 'win') return
    this.finishRun(true, false, false)
    this.toMenu()
  }

  private gameOver(): void {
    const world = this.world!
    this.state = 'gameover'
    this.endOverride()
    // a run that beat the campaign and died in overtime still counts as won
    this.finishRun(this.campaignWon, true)
    this.audio.stopMusic()
    this.hud.show(false)
    this.touchSource.setVisible(false)
    const isHigh = world.score > 0 && world.score >= this.bestScore
    const signOff = lineText(this.uiRng.pick(LINE_BANK.run_end))
    this.menus.showGameOver(
      world.score,
      this.floor,
      isHigh,
      signOff,
      this.mode,
      this.runStats,
      world.seed,
      this.dailyDate ?? undefined,
      this.amenityNames(),
      this.liveProvider.reportAvailable()
    )
    this.requestIncidentReport(this.campaignWon, world.score)
  }

  /**
   * Ask the live client for the end-of-run incident report and hand the
   * result to whichever end screen is showing (display-only; a null result
   * quietly collapses the block). Fire-and-forget: nothing waits on this.
   */
  private requestIncidentReport(won: boolean, score: number): void {
    if (!this.liveProvider.reportAvailable()) return
    void this.liveProvider
      .requestReport({
        won,
        score,
        timeSec: Math.round(this.runStats.runTime),
        kills: this.runStats.kills,
        archive: this.ownedFragments.size,
        daily: this.dailyDate !== null
      })
      .then((text) => this.menus.presentIncidentReport(text))
  }

  /**
   * Close out a run: emit run_end, persist the score entry (skipped when the
   * floor-15 win already banked an identical snapshot), accrue the dossier.
   * campaignWins is credited at the win moment itself (winCampaign), so an
   * abandoned overtime continuation never loses the victory.
   */
  private finishRun(won: boolean, died: boolean, persist = true): void {
    const world = this.world!
    this.bus.emit('run_end', { score: world.score, floor: this.floor, won })
    if (world.score > this.bestScore) this.bestScore = world.score
    if (persist) {
      void persistScore({
        score: world.score,
        floor: this.floor,
        mode: this.mode,
        won,
        date: Date.now(),
        seed: world.seed,
        ...(this.dailyDate ? { daily: this.dailyDate } : {})
      })
    }
    // residuals bank as a fraction of the final index — the meta-progression currency
    const earned = Math.floor(Math.max(0, world.score) * this.tuning.amenities.residualRate)
    if (earned > 0) {
      this.residuals += earned
      void kvSet('residuals', this.residuals)
    }
    // participant dossier accrues at run end (depth also advances per floor)
    const lt = this.lifetime
    lt.runs++
    lt.kills += this.runStats.kills
    lt.foodEaten += this.runStats.foodEaten
    lt.timeOnAir += this.runStats.runTime
    lt.deepestFloor = Math.max(lt.deepestFloor, this.floor)
    if (died) lt.deaths++
    void saveLifetime(lt)
  }

  private toMenu(): void {
    this.state = 'menu'
    this.endOverride()
    this.world = null
    this.audio.stopMusic()
    this.hud.show(false)
    this.touchSource.setVisible(false)
    this.menus.showMain(this.endlessUnlocked, this.bestScore, this.controlsHint())
    this.syncAttract()
  }

  /**
   * Attract mode runs exactly when the main menu is on screen and reduced
   * motion is off; the canvas dims under it so the menu stays readable.
   */
  private syncAttract(): void {
    // world === null guards the startRun window where state still reads 'menu'
    const want = this.state === 'menu' && this.world === null && !this.reducedMotionActive()
    if (want && !this.attract.current()) this.attract.start()
    if (!want) this.attract.stop()
    this.canvasEl.classList.toggle('attract-dim', want)
  }

  private reducedMotionActive(): boolean {
    return (
      this.settings.reducedMotion === 'on' ||
      (this.settings.reducedMotion === 'auto' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    )
  }

  private pause(): void {
    if (this.state !== 'playing') return
    this.state = 'paused'
    // presses queued while paused must never leak into the resumed sim
    this.desktopSource.clearQueued()
    this.touchSource.clearQueued()
    this.touchSource.setVisible(false)
    this.audio.setMusicDuck(true)
    this.menus.showPause(this.amenityNames())
  }

  private resume(): void {
    if (this.state !== 'paused') return
    this.state = 'playing'
    this.desktopSource.clearQueued()
    this.touchSource.clearQueued()
    this.audio.setMusicDuck(false)
    this.menus.hide()
    this.touchSource.setVisible(this.usingTouch)
  }

  // ——— per-tick ————————————————————————————————————————————————————

  private update(dt: number): void {
    this.daise.update(dt)
    if (this.state === 'vignette') {
      this.updateVignette(dt)
      return
    }
    if (this.state === 'menu') {
      this.attract.update(dt)
      return
    }
    const world = this.world
    if (!world || this.state !== 'playing') return

    world.levelTime += dt
    this.runStats.runTime += dt
    snapshotPositions(world)
    playerSystem(world, dt)

    // pause intent (read from the polled player intent)
    for (const p of world.players()) {
      if (p.player?.intent.pause) {
        this.pause()
        return
      }
      // OVERRIDE activation: full meter only, never mid-use or while cooling
      if (p.player?.intent.override && this.overrideMeter >= 1 && this.overrideT <= 0 && this.overrideCooldownT <= 0) {
        this.beginOverride()
      }
    }

    // OVERRIDE timers run in REAL time: while scaled, each sim tick of dt
    // corresponds to dt / timeScale real seconds
    if (this.overrideT > 0) {
      this.overrideT -= dt / this.tuning.override.timeScale
      if (this.overrideT <= 0) {
        this.endOverride()
        this.overrideCooldownT = this.tuning.override.cooldown
      }
    } else if (this.overrideCooldownT > 0) {
      this.overrideCooldownT -= dt
    }

    aiSystem(world, dt)
    generatorSystem(world, dt)
    projectileSystem(world, dt)
    pickupSystem(world, dt)
    amenitiesSystem(world, dt)
    attritionSystem(world, dt)
    bossSystem(world, dt)
    fxSystem(world, dt)

    // exit gating: generator-condition floors unlock when the last assembler dies
    if (!world.exitOpen && world.level.exit.type === 'generators' && world.generators().length === 0) {
      world.exitOpen = true
      this.audio.play('door')
    }

    // capture stats of players that died this tick (before sweep drops them)
    for (const e of world.entities) {
      if (e.kind === 'player' && e.dead && e.player) {
        // a spent life comes back at partial hp — dying is never a heal
        this.carried = { hp: this.tuning.player.respawnHp, magic: e.player.magic }
        this.carriedKeys = e.player.keys
      }
    }

    world.sweep()

    // high-score watermark mid-run; a first-ever run has no best to beat,
    // so it celebrates crossing a modest threshold instead.
    // DECISION: the comparison uses the CURRENT (post-requisition-spend) score
    // — the same number that persists at run end — and highEmitted latches
    // once per run, so spend/earn oscillation around bestScore never re-fires
    if (
      !this.highEmitted &&
      world.score > this.bestScore &&
      (this.bestScore > 0 || world.score >= this.tuning.daise.firstRunScoreLine)
    ) {
      this.highEmitted = true
      this.bus.emit('high_score', { score: world.score })
    }

    // death / respawn / run end
    if (world.players().length === 0) {
      if (this.respawnT <= 0) {
        this.lives--
        if (this.lives <= 0) {
          this.gameOver()
          return
        }
        this.bus.emit('life_lost', { remaining: this.lives })
        // DEAD MAN'S INVOICE: a lost life refunds part of the run's spending
        if (this.amenitiesOwned.has('invoice') && this.reqSpent > 0) {
          world.addScore(Math.round(this.reqSpent * this.tuning.amenities.invoice.refundRate))
        }
        this.respawnT = this.tuning.player.respawnDelay
      } else {
        this.respawnT -= dt
        if (this.respawnT <= 0) this.respawnPlayer()
      }
    }

    if (world.cleared) this.nextFloor()
  }

  private respawnPlayer(): void {
    const world = this.world!
    const spawn = world.level.playerSpawns[0]!
    const p = makePlayer(this.tuning, this.activeSource, spawn.x, spawn.y)
    p.maxHp = this.tuning.player.maxHp + world.mods.maxHpBonus // requisitioned chassis survives death
    p.hp = this.tuning.player.respawnHp // partial — see the death-carry note in update()
    if (this.carried) p.player!.magic = this.carried.magic
    p.player!.keys = this.carriedKeys
    world.add(p)
    world.sweep()
    this.respawnT = 0
  }

  private render(alpha: number): void {
    // menu: the attract broadcast plays dimmed behind the DOM overlay (no HUD)
    const attractWorld = this.state === 'menu' ? this.attract.current() : null
    if (attractWorld) this.renderer.render(attractWorld, alpha, TICK_S)
    const world = this.world
    if (world && this.state !== 'menu') {
      this.renderer.render(world, alpha, TICK_S)
      this.hud.update(world, this.lives, {
        meter: this.overrideMeter,
        active: this.overrideT > 0,
        cooling: this.overrideCooldownT > 0
      })
      this.dev.update(world, this.loop.fps)
    }
  }

  // ——— settings & audio ————————————————————————————————————————————

  private async applySettings(s: Settings, persist: boolean): Promise<void> {
    this.settings = s
    this.desktopSource.setClassicMode(s.classicAim)
    this.touchSource.setClassicMode(s.classicAim)
    this.audio.setVolumes(s.sfxVolume, s.musicVolume)
    this.liveProvider.setEnabled(s.liveCommentary)

    const wantTouch =
      s.inputOverride === 'touch' ||
      (s.inputOverride === 'auto' && window.matchMedia('(pointer: coarse)').matches && navigator.maxTouchPoints > 0)
    this.usingTouch = wantTouch
    const next: InputSource = wantTouch ? this.touchSource : this.desktopSource
    if (next !== this.activeSource) {
      this.activeSource = next
      // swap the live player's source in place (intent abstraction makes this safe)
      const p = this.world?.players()[0]
      if (p?.player) p.player.source = next
    }
    this.touchSource.setVisible(wantTouch && this.state === 'playing')
    this.applyWorldSettings()
    if (persist) await saveSettings(s)
  }

  private controlsHint(): string {
    return this.usingTouch
      ? 'LEFT STICK MOVES · RIGHT STICK AIMS + FIRES · ◆ DETONATES · AUTO-PICKUP'
      : 'WASD/ARROWS MOVE · MOUSE AIMS · CLICK FIRES · SPACE/RCLICK DETONATES'
  }

  private applyWorldSettings(): void {
    // the attract loop obeys the same reduced-motion contract as everything else
    this.syncAttract()
    if (!this.world) return
    const rm = this.reducedMotionActive()
    this.world.reducedMotion = rm
    this.world.shakeEnabled = this.settings.screenShake
    document.body.classList.toggle('reduced-motion', rm)
    this.dev.build(this.world)
  }

  /** acts 1-3 map to their music beds; deep overtime (act 4+) stays on act 3's */
  private actMusicKey(act = this.world?.level.act ?? 1): MusicKey {
    return act >= 3 ? 'act3' : act === 2 ? 'act2' : 'act1'
  }

  private wireAudio(): void {
    const a = this.audio
    this.bus.on('shot_fired', () => a.play('fire'))
    this.bus.on('player_hurt', () => a.play('hurt'))
    // hit confirms are throttled here (presentation concern — the sim just emits facts)
    let lastHitAt = 0
    this.bus.on('enemy_hit', () => {
      const now = performance.now()
      if (now - lastHitAt < 50) return
      lastHitAt = now
      a.play('hit')
    })
    this.bus.on('enemy_shot', () => a.play('enemyFire'))
    this.bus.on('enemy_killed', () => a.play('kill'))
    this.bus.on('pickup', ({ type }) => {
      if (type === 'food') a.play('food')
      else if (type === 'key') a.play('key')
      // DECISION: archive recoveries reuse the treasure chime — a new synth
      // recipe is not warranted for a pickup this rare
      else if (type === 'treasure' || type === 'archive') a.play('treasure')
      else a.play('magicPickup')
    })
    this.bus.on('magic_used', () => a.play('magic'))
    this.bus.on('requisition_purchase', () => a.play('treasure'))
    this.bus.on('generator_destroyed', () => a.play('genDown'))
    this.bus.on('door_opened', () => a.play('door'))
    this.bus.on('teleport', () => a.play('teleport'))
    this.bus.on('relay_latched', () => a.play('key'))
    this.bus.on('shutters_open', () => a.play('door'))
    this.bus.on('armament_pickup', () => a.play('magicPickup'))
    this.bus.on('armament_exhausted', () => a.play('uiMove'))
    this.bus.on('amenity_purchase', () => a.play('treasure'))
    this.bus.on('residual_unlock', () => a.play('levelClear'))
    // featured encounters get the sparser/heavier bed; the act bed returns on the kill
    this.bus.on('boss_intro', () => {
      a.play('bossIntro')
      a.setMusicPattern('boss')
    })
    this.bus.on('boss_kill', () => {
      a.play('bossKill')
      a.setMusicPattern(this.actMusicKey())
    })
    this.bus.on('player_death', () => a.play('playerDie'))
    this.bus.on('player_death_cheap', () => a.play('playerDie'))
    this.bus.on('level_clear', () => a.play('levelClear'))
    this.bus.on('death_type_spawn', () => a.play('auditor'))
    // low-vitals heartbeat — event cadence only (attrition re-emits every
    // daise.starvingRepeatEvery seconds while below the threshold)
    this.bus.on('player_starving', () => a.play('starving'))
  }

  // DECISION: Vibration API is Android-only on the web (iOS Safari has no
  // navigator.vibrate), so this degrades to a no-op there and on desktop.
  private wireHaptics(): void {
    if (typeof navigator.vibrate !== 'function') return
    // heavy double thump to match the charge blast's screen-clearing weight
    this.bus.on('magic_used', () => {
      if (this.settings.haptics) navigator.vibrate([80, 40, 140])
    })
  }
}
